import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { TabsBar } from "../components/TabsBar";

// ---------------------------------------------------------------------------
// Types — mirror public.drivers, public.assignments, public.vehicles
// ---------------------------------------------------------------------------

type Driver = {
  id: number;
  user_id: string;
  name: string;
  phone: string;
  active: boolean;
};

type Vehicle = {
  id: number;
  label: string;
};

type RouteRow = {
  id: number;
  name: string;
};

type Assignment = {
  id: number;
  driver_id: number;
  vehicle_id: number;
  route_id: number;
  valid_from: string;
  valid_to: string | null;
  drivers: { name: string } | null;
  vehicles: { label: string } | null;
  routes: { name: string } | null;
};

type DriverRow = Driver & {
  /** Label of the currently assigned bus, or null if none is open-ended / current. */
  assignedBusLabel: string | null;
};

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchDrivers(): Promise<DriverRow[]> {
  const [driversRes, vehiclesRes, assignmentsRes] = await Promise.all([
    supabase
      .from("drivers")
      .select("id, user_id, name, phone, active")
      .order("name", { ascending: true }),
    supabase.from("vehicles").select("id, label"),
    supabase
      .from("assignments")
      .select("id, driver_id, vehicle_id, route_id, valid_from, valid_to")
      .or(`valid_to.is.null,valid_to.gt.${new Date().toISOString()}`),
  ]);

  if (driversRes.error) throw driversRes.error;
  if (vehiclesRes.error) throw vehiclesRes.error;
  if (assignmentsRes.error) throw assignmentsRes.error;

  const drivers = (driversRes.data ?? []) as Driver[];
  const vehicles = (vehiclesRes.data ?? []) as Vehicle[];
  const assignments = (assignmentsRes.data ?? []) as unknown as Assignment[];

  const vehicleById = new Map<number, string>(
    vehicles.map((v) => [v.id, v.label]),
  );

  const currentByDriver = new Map<number, Assignment>();
  for (const a of assignments) {
    const existing = currentByDriver.get(a.driver_id);
    if (!existing) {
      currentByDriver.set(a.driver_id, a);
      continue;
    }
    if (existing.valid_to !== null && a.valid_to === null) {
      currentByDriver.set(a.driver_id, a);
      continue;
    }
    if (
      new Date(a.valid_from).getTime() >
      new Date(existing.valid_from).getTime()
    ) {
      currentByDriver.set(a.driver_id, a);
    }
  }

  return drivers.map<DriverRow>((d) => {
    const a = currentByDriver.get(d.id);
    return {
      ...d,
      assignedBusLabel: a ? vehicleById.get(a.vehicle_id) ?? null : null,
    };
  });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DriversPage() {
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Assignment form state
  const [driverId, setDriverId] = useState<number | null>(null);
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [routeId, setRouteId] = useState<number | null>(null);
  const [validFrom, setValidFrom] = useState<string>(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [validTo, setValidTo] = useState("");
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [endingId, setEndingId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [driverRows, vehicleRows, routeRows, assignRes] =
        await Promise.all([
          fetchDrivers(),
          supabase
            .from("vehicles")
            .select("id, label")
            .order("label", { ascending: true }),
          supabase
            .from("routes")
            .select("id, name")
            .order("name", { ascending: true }),
          supabase
            .from("assignments")
            .select(
              "id, driver_id, vehicle_id, route_id, valid_from, valid_to, drivers(name), vehicles(label), routes(name)",
            )
            .or(
              `valid_to.is.null,valid_to.gt.${new Date().toISOString()}`,
            )
            .order("valid_from", { ascending: false }),
        ]);
      setDrivers(driverRows);

      if (vehicleRows.error) throw vehicleRows.error;
      if (routeRows.error) throw routeRows.error;
      if (assignRes.error) throw assignRes.error;

      setVehicles((vehicleRows.data ?? []) as Vehicle[]);
      setRoutes((routeRows.data ?? []) as RouteRow[]);
      setAssignments((assignRes.data ?? []) as unknown as Assignment[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onToggleActive = async (d: DriverRow) => {
    setTogglingId(d.id);
    const { error: updErr } = await supabase
      .from("drivers")
      .update({ active: !d.active })
      .eq("id", d.id);
    setTogglingId(null);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    void refresh();
  };

  const onCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    setAssignError(null);
    if (!driverId || !vehicleId || !routeId) {
      setAssignError("Driver, vehicle, and route are required.");
      return;
    }
    setAssignSubmitting(true);
    const { error: insErr } = await supabase.from("assignments").insert({
      driver_id: driverId,
      vehicle_id: vehicleId,
      route_id: routeId,
      valid_from: new Date(validFrom).toISOString(),
      valid_to: validTo ? new Date(validTo).toISOString() : null,
    });
    setAssignSubmitting(false);
    if (insErr) {
      setAssignError(insErr.message);
      return;
    }
    setDriverId(null);
    setVehicleId(null);
    setRouteId(null);
    setValidTo("");
    void refresh();
  };

  const onEndAssignment = async (id: number) => {
    if (!window.confirm("End this assignment now?")) return;
    setEndingId(id);
    const { error: updErr } = await supabase
      .from("assignments")
      .update({ valid_to: new Date().toISOString() })
      .eq("id", id);
    setEndingId(null);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    void refresh();
  };

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Drivers &amp; assignments</h1>
          <p style={styles.muted}>
            Manage drivers and their bus-route assignments.
          </p>
        </div>
        <div style={styles.headerRight}>
          <Link to="/dashboard" style={styles.linkButton}>
            ← Dashboard
          </Link>
        </div>
      </header>

      <TabsBar active="drivers" />

      {error && (
        <div
          style={{
            ...styles.alert,
            background: "#fdecea",
            color: "#a02622",
            borderColor: "#e0a3a0",
          }}
        >
          {error}
        </div>
      )}

      {/* Drivers list */}
      <section style={styles.card}>
        <h2 style={styles.h2}>All drivers</h2>

        {loading ? (
          <p style={styles.muted}>Loading drivers…</p>
        ) : drivers.length === 0 ? (
          <p style={styles.muted}>No drivers yet.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Assigned bus</th>
                <th style={styles.th}>Active</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.id}>
                  <td style={styles.td}>
                    <strong>{d.name}</strong>
                  </td>
                  <td style={styles.td}>
                    <code style={styles.muted}>{d.phone}</code>
                  </td>
                  <td style={styles.td}>
                    {d.assignedBusLabel ? (
                      d.assignedBusLabel
                    ) : (
                      <span style={styles.muted}>— unassigned</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      onClick={() => void onToggleActive(d)}
                      disabled={togglingId === d.id}
                      style={{
                        ...styles.badge,
                        background: d.active ? "#1f9d55" : "#9aa0a6",
                        cursor:
                          togglingId === d.id ? "wait" : "pointer",
                        border: "none",
                      }}
                      aria-pressed={d.active}
                    >
                      {togglingId === d.id
                        ? "…"
                        : d.active
                          ? "ACTIVE"
                          : "inactive"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Create assignment */}
      <section style={styles.card}>
        <h2 style={styles.h2}>Create assignment</h2>
        <form onSubmit={onCreateAssignment} style={styles.form}>
          <div style={styles.formRow}>
            <label style={styles.label}>
              Driver
              <select
                value={driverId ?? ""}
                onChange={(e) =>
                  setDriverId(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                style={styles.input}
                required
              >
                <option value="">Select driver…</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.label}>
              Vehicle
              <select
                value={vehicleId ?? ""}
                onChange={(e) =>
                  setVehicleId(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                style={styles.input}
                required
              >
                <option value="">Select vehicle…</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.label}>
              Route
              <select
                value={routeId ?? ""}
                onChange={(e) =>
                  setRouteId(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                style={styles.input}
                required
              >
                <option value="">Select route…</option>
                {routes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={styles.formRow}>
            <label style={styles.label}>
              Valid from
              <input
                type="datetime-local"
                required
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                style={styles.input}
              />
            </label>
            <label style={styles.label}>
              Valid to (optional)
              <input
                type="datetime-local"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                style={styles.input}
              />
            </label>
          </div>
          {assignError && <p style={styles.error}>{assignError}</p>}
          <div style={styles.formActions}>
            <button
              type="submit"
              disabled={assignSubmitting}
              style={styles.button}
            >
              {assignSubmitting ? "Saving…" : "Create assignment"}
            </button>
          </div>
        </form>
      </section>

      {/* Current assignments */}
      <section style={styles.card}>
        <h2 style={styles.h2}>Current assignments</h2>
        {assignments.length === 0 ? (
          <p style={styles.muted}>No active assignments.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Driver</th>
                <th style={styles.th}>Vehicle</th>
                <th style={styles.th}>Route</th>
                <th style={styles.th}>Valid from</th>
                <th style={styles.th} aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td style={styles.td}>
                    {a.drivers?.name ?? "—"}
                  </td>
                  <td style={styles.td}>
                    {a.vehicles?.label ?? "—"}
                  </td>
                  <td style={styles.td}>
                    {a.routes?.name ?? "—"}
                  </td>
                  <td style={styles.td}>
                    {new Date(a.valid_from).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => void onEndAssignment(a.id)}
                      disabled={endingId === a.id}
                      style={styles.dangerButton}
                    >
                      {endingId === a.id ? "Ending…" : "End now"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

export default DriversPage;

// ---------------------------------------------------------------------------
// Styles — inline (zero extra deps)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: 24,
    color: "#1a1a1a",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  h1: { margin: 0, fontSize: 20 },
  h2: { margin: 0, fontSize: 16 },
  muted: { color: "#666", fontSize: 14, margin: 0 },
  alert: {
    padding: "10px 14px",
    border: "1px solid",
    borderRadius: 6,
    marginBottom: 16,
    fontWeight: 600,
  },
  card: {
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    marginBottom: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  linkButton: {
    padding: "6px 10px",
    background: "#f1f1f1",
    color: "#1a1a1a",
    border: "1px solid #ccc",
    borderRadius: 4,
    textDecoration: "none",
    fontSize: 13,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #1a3a5c",
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    color: "#fff",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 12,
    border: "1px solid #eee",
    borderRadius: 6,
    background: "#fafafa",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 12,
  },
  formActions: { display: "flex", justifyContent: "flex-end" },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 14 },
  input: {
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    fontSize: 14,
  },
  button: {
    padding: "8px 14px",
    background: "#1a3a5c",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
  },
  dangerButton: {
    padding: "4px 10px",
    background: "#fff",
    color: "#c0392b",
    border: "1px solid #c0392b",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  error: { color: "#c0392b", fontSize: 13, margin: 0 },
};
